/**
 * @format
 */
import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient, HttpHeaders, HttpUrlEncodingCodec } from "@angular/common/http";
import { Observable, Subscription } from "rxjs";
import {
   CheckImageNameAndExistenceResponse,
   DirStructureResponse,
   ImageOuterHTMLType,
   ImageTreeNode,
   UseImage,
} from "../types";
import { map } from "rxjs/operators";
import { BlobFile } from "ngx-uploader";
import { UserSelectors } from "../../user/state/user.selectors";
import { BsModalAlertComponent, createAlertModalOptions } from "../../core/modal/modal-alert.component";
import { BsModalService } from "ngx-bootstrap/modal";

export class ImageSize {
   readonly _width: string;
   readonly _height: string;

   constructor(width: string | number, height: string | number) {
      this._width = typeof width === "number" ? width.toString() : width.replace(/px/g, "");
      this._height = typeof height === "number" ? height.toString() : height.replace(/px/g, "");
   }

   getWidth(): string {
      return `${this._width}px`;
   }

   getHeight(): string {
      return `${this._height}px`;
   }
}

@Injectable()
export class TemplateImagesService implements OnDestroy {
   encodingCodec: HttpUrlEncodingCodec;
   _sub: Subscription = new Subscription();
   private selectedUserId: string = "";
   readonly DEFAULT_WIDTH = "600";
   readonly DEFAULT_HEIGHT = "300";

   constructor(
      private httpClient: HttpClient,
      private userSelectors: UserSelectors,
      private modalService: BsModalService
   ) {
      this.encodingCodec = new HttpUrlEncodingCodec();

      this._sub.add(
         this.userSelectors.selectSelectedUser().subscribe((userSelected) => {
            if (userSelected) {
               this.selectedUserId = `${userSelected.userId}`;
            }
         })
      );
   }

   cancelTempUploadedImages(temporaryFileName: string): Observable<any> {
      const templateUrl = "/servlets/templates/deleteimage/";
      const options = {
         body: {
            fileName: temporaryFileName,
         },
         headers: new HttpHeaders({ ["Content-Type"]: "application/json" }),
      };

      return this.httpClient.delete(templateUrl, { ...options });
   }

   checkImageNameAndExistence(imageName: string): Observable<any> {
      const url = `/servlets/templates/checkimagenameandexistence/?fileName=${this.encodingCodec.encodeValue(
         imageName
      )}&userFolder=`;

      return this.httpClient
         .get<CheckImageNameAndExistenceResponse>(url, {
            observe: "body",
            responseType: "json",
         })
         .pipe(map((resp) => resp));
   }

   useImages(imagesList: Array<UseImage>): Observable<any> {
      const useImagesUrl = "/servlets/templates/useimage/";
      return this.httpClient.post(useImagesUrl, { useImagesList: imagesList });
   }

   getImagesTree(userId: string): Observable<Array<ImageTreeNode>> {
      const url = `/servlets/templates/dirstructure/?path=/${this.encodingCodec.encodeValue(userId)}/`;

      return this.httpClient.get<DirStructureResponse>(url, { observe: "body", responseType: "json" }).pipe(
         map((resp) => {
            return resp.directoryStructure.map<ImageTreeNode>(
               (imgNode) => new ImageTreeNode({ label: imgNode.label, type: imgNode.type })
            );
         })
      );
   }

   uploadImage(imageFile: File, name: string): Observable<any> {
      const useImagesUrl = "/servlets/templates/upload/";
      const form = new FormData();
      const file = <BlobFile>imageFile;
      form.append("fileName", name);
      form.append("file", file, file.name);
      return this.httpClient.post(useImagesUrl, form);
   }

   getSelectedUserIdFromURL(): string {
      return this.selectedUserId;
   }

   ngOnDestroy(): void {
      this._sub.unsubscribe();
   }

   openAlertMaxSizeExceed(fileName: string): void {
      const message = `The file ${fileName} exceeds the maximum size of 1MB`;
      this.modalService.show(BsModalAlertComponent, createAlertModalOptions("Image not supported", message));
   }

   getImgElement(imageHTMLAsString: string): Element {
      const auxSpanElement = document.createElement("span");
      auxSpanElement.innerHTML = imageHTMLAsString;

      return auxSpanElement.firstElementChild;
   }

   getImagesFilterByAttributeFromElement(element: HTMLElement, attribute: string): Array<HTMLImageElement> {
      const imgCollectionElements = element.getElementsByTagName("img");
      const imgRefElements = Array.from(imgCollectionElements).filter((imgElement) =>
         Boolean(imgElement.getAttribute(attribute))
      );
      return imgRefElements;
   }

   getImageRefFromEvent(event: Event): HTMLImageElement {
      return Array.from((<Element>event.currentTarget).parentElement.getElementsByTagName("img"))[0];
   }

   getInputFromEvent(event: Event): HTMLInputElement {
      return Array.from((<Element>event.currentTarget).parentElement.getElementsByTagName("input"))[0];
   }

   getNgModelValueFromImage(imgElement: HTMLImageElement): string {
      return imgElement.getAttribute("ng-model").split(".")[1];
   }

   getNewImageOuterHTML(imageOuterHTMLType: ImageOuterHTMLType): string {
      const { stylesCallback, imageContainerClass, inputId, buttonId, imageElementAsString } = imageOuterHTMLType;
      return `
           ${stylesCallback()}
             <div class="${imageContainerClass}">
                ${imageElementAsString}
                <input type="file"
                        id="${inputId}"
                        style="display: none"
                        accept="image/*"
                />
                <button class="icon__container" id="${buttonId}">
                    <span class="glyphicon glyphicon-upload">
                       <span class="tooltiptext" data-text="Upload image by clicking or dropping"></span>
                    </span>
                </button>
              </div>
        `;
   }

   async getImageSize(imgElementRef: HTMLImageElement): Promise<ImageSize> {
      const srcURL = imgElementRef.getAttribute("src");
      let height = "";
      let width = "";

      if (this.isImagURLValid(srcURL)) {
         const imageSizeFromURL = await this.getImageFromURL(srcURL);
         height = imageSizeFromURL.height.toString();
         width = imageSizeFromURL.width.toString();
      }

      if (Boolean(imgElementRef.getAttribute("height"))) {
         const matched = imgElementRef.getAttribute("height").match(/([0-9])+/g);
         height = Boolean(matched) ? matched[0] : height;
      }

      if (Boolean(imgElementRef.getAttribute("width"))) {
         const matched = imgElementRef.getAttribute("width").match(/([0-9])+/g);
         width = Boolean(matched) ? matched[0] : width;
      }

      const preventedZeroHeight = this.checkZeroOrDefault(height, this.DEFAULT_HEIGHT);
      const preventZeroWidth = this.checkZeroOrDefault(width, this.DEFAULT_WIDTH);
      return new ImageSize(preventZeroWidth, preventedZeroHeight);
   }

   private isImagURLValid(srcURL: string): boolean {
      try {
         return Boolean(new URL(srcURL));
      } catch (error) {
         return false;
      }
   }

   private getImageFromURL(url): Promise<HTMLImageElement> {
      return new Promise((resolve, reject) => {
         let img = new Image();
         img.onload = () => resolve(img);
         img.onerror = () => reject();
         img.src = url;
      });
   }

   private checkZeroOrDefault(value: string, defaultValue: string): string {
      if (!Boolean(value) || !Boolean(value.match(/([0-9])+/g))) {
         return defaultValue;
      }

      let auxValue = value.match(/([0-9])+/g)[0];
      auxValue = value.replace(/px/g, "");
      auxValue = auxValue.replace(/0/g, "");
      return Boolean(auxValue) ? value : defaultValue;
   }
}
